/*
 * Copyright (c) 2014-2024 Comparatio USA  LLC. <https://www.comparatio.com>
 */

import {Component, OnInit, ViewChild} from '@angular/core';
import {Page} from '../../model/page';
import {permissions} from '../../permissions';
import {ActivatedRoute, ActivatedRouteSnapshot, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {ShipmentHeadersService} from '../../services/shipment-headers.service';
import {OrderHeadersService} from '../../services/order-headers.service';
import {Title} from '@angular/platform-browser';
import * as _ from 'lodash';

@Component({
  selector: 'app-shipment-headers',
  templateUrl: './shipment-headers.component.html',
  styleUrls: ['./shipment-headers.component.scss'],
  providers: [
    ShipmentHeadersService,
    OrderHeadersService
  ]
})
export class ShipmentHeadersComponent implements OnInit {

    @ViewChild('myTable') table: any;

    public loading = false;
    public  permissions = permissions;

    PO1_ID: number = null;
    po: any = {};
    page = new Page();
    rows = new Array<object>();
    cols = [];
    totalRecords = 0;
    selected = [];

    private companies: Array<any> = [];
    private vendors:   Array<any> = [];

    private filters: { [key: string]: any; } = {};
    public viewFilters: boolean;
    public filtersApplied: boolean;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private shipmentHeadersService: ShipmentHeadersService,
        private orderHeadersService: OrderHeadersService,
        private toastr: ToastrService,
        private title: Title
    ) {
        this.page.pageNumber = 0;
        this.page.size = 25;
        this.title.setTitle('EDI - Shipments');
    }

    ngOnInit() {
        this.viewFilters = false;
        this.filtersApplied = false;
        this.cols = [
            { field: 'PO1_NUMBER',           header: 'PO Number',        visible: true,  width: 75  },
            { field: 'SH1_TRACKING_NUMBER',  header: 'Tracking Number',  visible: true,  width: 150 },
            { field: 'SH1_INTERNAL_ID',      header: 'Shipment Number',  visible: true,  width: 100 },
            { field: 'CO1_ID',               header: 'Company',          visible: true,  width: 100 },
            { field: 'VD1_ID',               header: 'Vendor',           visible: true,  width: 100  },
            { field: 'SH1_CREATED_ON',       header: 'Created On',       visible: true,  width: 100 }
        ];

        const filterParams = this.route.snapshot.queryParams;
        this.parseParams( filterParams );

        this.shipmentHeadersService.preloadData().subscribe(data => {
          this.prepareData(data);
          this.getData(false);
        });
    }

    parseParams( filterParams ) {
        if (filterParams !== undefined && filterParams !== null) {
            let showFilters = false;
            for (const key of Object.keys(filterParams)) {
                const value = filterParams[key];
                if (value && value.toString() !== '') {
                    if (key.toString() === 'page') {
                        this.page.pageNumber = Number(value);
                    } else if (key.toString() === 'perPage') {
                        this.page.size = Number(value);
                    } else {
                        this.page.filterParams[key] = value;
                        this.filters[key.toString()] = value.toString();
                        showFilters = true;
                    }
                }
            }
            this.viewFilters = showFilters;
            this.filtersApplied = showFilters;
        }
    }

    prepareData( data ) {
        this.companies = data.companies.map( company => {
            return {
                CO1_ID: company.CO1_ID,
                text:   company.CO1_NAME
            }
        });

        this.vendors   = data.vendors.map( vendor => {
            const text  = (vendor.CO1_NAME ? vendor.CO1_NAME + ': ' : '') +
                        (vendor.VD1_NAME ? vendor.VD1_NAME : '');
            return {
                value:  vendor.CO1_ID + '_' + vendor.VD1_ID,
                VD1_ID: Number(vendor.VD1_ID),
                CO1_ID: Number(vendor.CO1_ID),
                text:   text,
                displayFld: vendor.VD1_NAME
            }
        }).filter( vendor => {
            if (
                this.filters.CO1_ID
                && this.filters.CO1_ID.length
            ) {
                return this.filters.CO1_ID.indexOf(vendor.CO1_ID) > -1;
            } else {
                return true;
            }
        });
    }

    getData(reloadWithParams = true) {
        this.setPage({offset: this.page.pageNumber}, reloadWithParams);
    }

    loadLines(SH1_INTERNAL_ID) {
        this.router.navigate(['shipment-headers/view/' + SH1_INTERNAL_ID]);
    }

    setPage(pageInfo, reloadWithParams = true) {
        if (reloadWithParams) {
            this.page.filterParams['page'] = pageInfo.offset;
            this.page.filterParams['perPage'] = this.page.size;
            this.router.routeReuseStrategy.shouldReuseRoute = function (future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot) {
                return false;
            }
            this.router.navigate([], { relativeTo: this.route, queryParams: this.page.filterParams });
        } else {
            if (this.route.snapshot.params.id) {
                this.page.filterParams = {
                    PO1_ID: this.route.snapshot.params.id
                }
            }
            this.loading = true;
            this.shipmentHeadersService.getItems(this.page).subscribe(result => {
                this.rows = result.data;
                this.totalRecords = result.count;
                this.loading = false;
            }, error => {
                this.loading = false;
                window.dispatchEvent(new Event('resize'));
                this.toastr.error('TimeOut occured', 'Service Error', {
                    timeOut: 3000,
                });
            });
        }
    }

    onPage(event) {
        this.page.size = event.rows;
        this.setPage({
            offset: event.first / event.rows
        });
    }

    openPO(PO1_ID) {
        this.router.navigate(['order-headers/' + PO1_ID]);
    }

    onCancel() {
        this.router.navigate(['order-headers/' + this.PO1_ID]);
    }

    clearFilters() {
        this.filters = {};
        this.filtersApplied = false;
        this.viewFilters = false;

        this.applyFilters();
    }

    applyFilters() {
        const filters = _.clone(this.filters);
        for (const filterKey of Object.keys(filters)) {
            if (filters[filterKey] == null || filters[filterKey] === undefined || filters[filterKey] === '') {
                delete filters[filterKey];
            }
        }
        this.filtersApplied = Object.keys(filters).length > 0;
        this.page.filterParams = filters;
        this.setPage({offset: 0});
    }

    toggleFilters() {
        this.viewFilters = !this.viewFilters;
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/shipment-headers/shipment-headers.component.ts